import React from "react";

/**
 * Courtesy limits on the Troubleshooter's buttons. Every active check is an
 * SSM round trip (or a login attempt against the customer's device), so a
 * user hammering Run fills the probe queue for everyone and can lock a
 * device account. The server's own limits are the real control (credential
 * lockout guard, WireGuard scan cooldown, receiver test cooldown); these
 * keep an honest user from tripping them and make a server 429 visible.
 */
export const RUN_ALL_COOLDOWN_SECONDS = 30;
export const PORT_CHECK_COOLDOWN_SECONDS = 10;
export const CREDENTIAL_CHECK_COOLDOWN_SECONDS = 15;
export const S2S_CHECK_COOLDOWN_SECONDS = 20;

/** Seconds left before the button may fire again; `start` begins a wait. */
export const useCooldown = () => {
  const [until, setUntil] = React.useState(0);
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (until <= Date.now()) return undefined;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [until]);
  const start = React.useCallback((seconds: number) => {
    if (!(seconds > 0)) return;
    const t = Date.now();
    setNow(t);
    setUntil((prev) => Math.max(prev, t + seconds * 1000));
  }, []);
  const remaining = Math.max(0, Math.ceil((until - now) / 1000));
  return { remaining, start };
};

/** Label for a button that is waiting out its cooldown. */
export const waitLabel = (remaining: number) => `Wait ${remaining} s`;
